import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { format, differenceInDays, addYears, isBefore } from "date-fns";
import { 
  Gift, Calendar, Users, ArrowLeft, AlertCircle, Clock, CheckCircle, 
  UserPlus, Bell, BellOff, LogOut, Crown, Shield, Trash2, MessageSquare, 
  Lightbulb, DollarSign, CreditCard
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CustomerHeader } from "@/components/CustomerHeader";
import { CustomerFooter } from "@/components/CustomerFooter";
import { SEO } from "@/components/SEO";
import { SocialShareSection } from "@/components/SocialShareSection";
import { validateEmail } from "@/lib/emailValidation";
import { ParticipantLimitDialog, FREE_PARTICIPANT_LIMIT_CONST } from "@/components/ParticipantLimitDialog";
import { InviteLinkCopyButton } from "@/components/InviteLinkCopyButton";
// GroupPaymentSection removed in professional-only version
import { DiscussionChat } from "@/components/discussion/DiscussionChat";
import { DiscussionIdeas } from "@/components/discussion/DiscussionIdeas";
import { DiscussionBudget } from "@/components/discussion/DiscussionBudget";
import { DiscussionPeople } from "@/components/discussion/DiscussionPeople";
import { DiscussionActionBar } from "@/components/discussion/DiscussionActionBar";
import { AddGroupPaymentDialog } from "@/components/AddGroupPaymentDialog";

interface Reminder {
  id: string;
  event_name: string;
  recipient_name: string | null;
  event_date: string;
  is_recurring: boolean;
  customer_email: string | null;
  reminder_email: string | null;
  invited_friend_email: string | null;
  invited_friend_status: string | null;
  occasion: string | null;
  payment_enabled?: boolean;
  payment_type?: string;
  split_type?: string;
  target_amount?: number | null;
  collected_amount?: number;
  currency?: string;
  payment_link?: string | null;
  organizer_upi_id?: string | null;
  payment_deadline?: string | null;
  reminder_hours_before?: number[];
  payment_mode?: string;
  max_free_participants?: number;
  paid_participant_slots?: number;
  participant_fee_paid_by?: string;
  // Payment gateway fields
  razorpay_linked_account_id?: string | null;
  stripe_connected_account_id?: string | null;
  payment_provider?: 'razorpay' | 'stripe' | null;
  platform_fee_per_participant?: number | null;
  // Invite link fields
  invite_link_enabled?: boolean;
  invite_link_token?: string;
  invite_link_approval_mode?: string;
  max_self_invites?: number | null;
}

interface Invitee {
  id: string;
  email: string;
  name: string | null;
  invited_at: string;
  status: string;
  role: 'organizer' | 'admin' | 'participant';
  has_paid?: boolean;
  contribution_amount?: number | null;
  paid_at?: string | null;
  payment_proof_url?: string | null;
}

const GIFT_COLORS = {
  gradient: "from-pink-500 via-rose-500 to-pink-600",
  tipBg: "bg-pink-500/5",
  tipBorder: "border-pink-500/10",
  accent: "text-pink-600",
};

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳" },
  { code: "+1", flag: "🇺🇸" },
  { code: "+44", flag: "🇬🇧" },
  { code: "+971", flag: "🇦🇪" },
];

const SESSION_KEYS = ["shakatah_gift_session", "shakatah_customer_session"];

interface StoredSession {
  email?: string;
  phone?: string;
  method: "email" | "phone";
  expiresAt?: string;
}


const EventDiscussion = () => {
  const { reminderId } = useParams<{ reminderId: string }>();
  const [searchParams] = useSearchParams();
  const [reminder, setReminder] = useState<Reminder | null>(null);
  const [invitees, setInvitees] = useState<Invitee[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState("");
  const [newInviteName, setNewInviteName] = useState("");
  const [newInvitePhone, setNewInvitePhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [inviting, setInviting] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [leavingEvent, setLeavingEvent] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [participantLimitDialogOpen, setParticipantLimitDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingEvent, setCancellingEvent] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [collectPaymentDialogOpen, setCollectPaymentDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (reminderId) {
      fetchReminder();
    }
  }, [reminderId]);

  // Handle token-based auto-verification from email links
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token || !reminderId || isVerified) return;

    const verifyByToken = async () => {
      try {
        // Check if token matches any invitee
        const { data: invitee, error } = await supabase
          .from("event_invitees")
          .select("email, name, status")
          .eq("reminder_id", reminderId)
          .eq("access_token", token)
          .single();

        if (error || !invitee) {
          console.log("Token verification failed:", error);
          return;
        }

        if (invitee.status !== 'active') {
          toast.error("Your participation has been cancelled");
          return;
        }

        // Token valid - auto-login
        const session = {
          email: invitee.email.toLowerCase(),
          method: "email" as const,
        };
        sessionStorage.setItem("shakatah_customer_session", JSON.stringify(session));
        
        setUserEmail(invitee.email.toLowerCase());
        setUserName(invitee.name || invitee.email.split('@')[0]);
        setIsVerified(true);
        toast.success("Welcome! You've been verified via email link.");
        
        // Clean up URL
        navigate(`/events/${reminderId}`, { replace: true });
      } catch (err) {
        console.error("Token verification error:", err);
      }
    };

    verifyByToken();
  }, [searchParams, reminderId, isVerified]);

  useEffect(() => {
    if (reminder && !isVerified) {
      checkExistingSession();
    }
  }, [reminder]);

  useEffect(() => {
    if (reminder && !isVerified) {
      checkExistingSession();
    }
  }, [reminder]);

  const checkExistingSession = async () => {
    for (const sessionKey of SESSION_KEYS) {
      const storedSession = sessionStorage.getItem(sessionKey);
      if (!storedSession) continue;

      try {
        const session: StoredSession = JSON.parse(storedSession);
        if (session.expiresAt && new Date(session.expiresAt) <= new Date()) {
          sessionStorage.removeItem(sessionKey);
          continue;
        }

        const sessionEmail = session.email?.toLowerCase();
        if (!sessionEmail) continue;

        const baseEmails = [
          reminder?.customer_email?.toLowerCase(),
          reminder?.reminder_email?.toLowerCase(),
          reminder?.invited_friend_email?.toLowerCase(),
        ].filter(Boolean);

        if (baseEmails.includes(sessionEmail)) {
          setUserEmail(sessionEmail);
          setIsVerified(true);
          return;
        }

        const { data: inviteesData } = await supabase
          .from("event_invitees" as any)
          .select("email")
          .eq("reminder_id", reminderId);

        const inviteeEmails = (inviteesData || []).map((i: any) => i.email.toLowerCase());
        if (inviteeEmails.includes(sessionEmail)) {
          setUserEmail(sessionEmail);
          setIsVerified(true);
          return;
        }
      } catch {
        // Continue to next key
      }
    }
  };

  useEffect(() => {
    if (isVerified && reminderId) {
      fetchInvitees();
      checkPushSubscription();
    }
  }, [isVerified, reminderId]);

  const fetchReminder = async () => {
    try {
      const { data, error } = await supabase
        .from("gift_reminders")
        .select("*")
        .eq("id", reminderId)
        .single();

      if (error) throw error;
      setReminder(data as unknown as Reminder);
    } catch (error) {
      console.error("Error fetching reminder:", error);
      toast.error("Event not found");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitees = async () => {
    try {
      const { data, error } = await supabase
        .from("event_invitees" as any)
        .select("*")
        .eq("reminder_id", reminderId)
        .order("invited_at", { ascending: true }) as any;

      if (error) throw error;
      setInvitees((data || []) as Invitee[]);
    } catch (error) {
      console.error("Error fetching invitees:", error);
    }
  };

  const checkPushSubscription = async () => {
    if (!('Notification' in window)) return;
    setPushEnabled(Notification.permission === 'granted');
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      toast.error("Push notifications not supported");
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setPushEnabled(true);
      toast.success("Notifications enabled!");
    }
  };

  const verifyAccess = async () => {
    if (!userEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }

    const email = userEmail.toLowerCase().trim();
    const baseEmails = [
      reminder?.customer_email?.toLowerCase(),
      reminder?.reminder_email?.toLowerCase(),
      reminder?.invited_friend_email?.toLowerCase(),
    ].filter(Boolean);

    const storeSessionAndVerify = (verifiedEmail: string) => {
      const session = { email: verifiedEmail, method: "email" as const };
      sessionStorage.setItem("shakatah_customer_session", JSON.stringify(session));
      setIsVerified(true);
      toast.success("Welcome!");
    };

    if (baseEmails.includes(email)) {
      storeSessionAndVerify(email);
    } else {
      const { data } = await supabase
        .from("event_invitees" as any)
        .select("email")
        .eq("reminder_id", reminderId);

      const inviteeEmails = (data || []).map((i: any) => i.email.toLowerCase());
      if (inviteeEmails.includes(email)) {
        storeSessionAndVerify(email);
      } else {
        toast.error("You don't have access to this event");
      }
    }
  };

  const inviteFriend = async () => {
    const email = newInviteEmail.trim();
    if (!email) {
      toast.error("Please enter an email");
      return;
    }

    const validation = validateEmail(email);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid email");
      return;
    }

    // Check participant limit
    const maxFree = reminder?.max_free_participants ?? FREE_PARTICIPANT_LIMIT_CONST;
    const paidSlots = reminder?.paid_participant_slots ?? 0;
    const totalSlots = maxFree + paidSlots;
    const currentCount = activeInvitees.length + 1; // +1 for organizer
    
    if (currentCount >= totalSlots) {
      // Limit reached - show upgrade dialog
      setInviteDialogOpen(false);
      setParticipantLimitDialogOpen(true);
      return;
    }

    setInviting(true);
    try {
      const fullPhone = newInvitePhone.trim() 
        ? `${phoneCountryCode}${newInvitePhone.replace(/^0+/, '')}` 
        : null;

      const { error } = await supabase
        .from("event_invitees" as any)
        .insert({
          reminder_id: reminderId,
          email: newInviteEmail.toLowerCase().trim(),
          name: newInviteName.trim() || null,
          phone: fullPhone,
          invited_by: userEmail.toLowerCase(),
        } as any);

      if (error?.message?.includes('duplicate')) {
        toast.error("Already invited");
        return;
      }
      if (error) throw error;

      try {
        await supabase.functions.invoke("send-event-invitation", {
          body: { 
            reminderId, 
            type: 'additional',
            inviteeEmail: newInviteEmail.toLowerCase().trim(),
            inviteeName: newInviteName.trim() || null,
            invitedBy: userName || userEmail
          }
        });
      } catch (e) {
        console.error("Failed to send invitation email:", e);
      }

      toast.success("Friend invited!");
      setInviteDialogOpen(false);
      setNewInviteEmail("");
      setNewInviteName("");
      setNewInvitePhone("");
      fetchInvitees();
    } catch (error) {
      console.error("Error inviting:", error);
      toast.error("Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("shakatah_customer_session");
    sessionStorage.removeItem("shakatah_gift_session");
    navigate("/my-purchases");
  };

  const leaveEvent = async () => {
    setLeavingEvent(true);
    try {
      const { error } = await supabase
        .from("event_invitees" as any)
        .update({ status: 'opted_out' } as any)
        .eq("reminder_id", reminderId)
        .eq("email", userEmail.toLowerCase());

      if (error) throw error;
      toast.success("You have left this event");
      window.location.href = "/my-purchases";
    } catch (error) {
      console.error("Error leaving:", error);
      toast.error("Failed to leave");
    } finally {
      setLeavingEvent(false);
    }
  };

  const cancelEvent = async () => {
    if (!reminderId || !isOrganizer || !reminder) return;
    
    setCancellingEvent(true);
    try {
      // Collect participant emails before deleting
      const participantsToNotify = activeInvitees.map(i => ({
        email: i.email,
        name: i.name
      }));

      // Send cancellation emails to all participants
      if (participantsToNotify.length > 0) {
        try {
          await supabase.functions.invoke("notify-event-cancelled", {
            body: {
              eventId: reminderId,
              eventName: reminder.event_name,
              eventType: 'gift_event',
              organizerName: userEmail.split('@')[0],
              organizerEmail: userEmail,
              participants: participantsToNotify,
              reason: cancelMessage.trim() || undefined,
            },
          });
        } catch (emailError) {
          console.error("Error sending cancellation emails:", emailError);
          // Continue with deletion even if emails fail
        }
      }
      
      // Reset cancel message
      setCancelMessage("");

      // Delete the gift reminder (this will cascade to event_invitees due to RLS)
      const { error } = await supabase
        .from("gift_reminders")
        .delete()
        .eq("id", reminderId);

      if (error) throw error;
      
      toast.success("Event cancelled successfully. All participants have been notified.");
      navigate("/my-purchases");
    } catch (error) {
      console.error("Error cancelling event:", error);
      toast.error("Failed to cancel event");
    } finally {
      setCancellingEvent(false);
      setCancelDialogOpen(false);
    }
  };

  const getNextOccurrence = () => {
    if (!reminder) return null;
    const eventDate = new Date(reminder.event_date);
    const today = new Date();
    
    if (reminder.is_recurring) {
      let nextDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
      if (isBefore(nextDate, today)) {
        nextDate = addYears(nextDate, 1);
      }
      return nextDate;
    }
    return eventDate;
  };

  const getDaysUntil = () => {
    const nextDate = getNextOccurrence();
    if (!nextDate) return 0;
    return differenceInDays(nextDate, new Date());
  };


  // Determine user role
  const isOrganizer = reminder?.customer_email?.toLowerCase() === userEmail.toLowerCase();
  const isAdmin = invitees.some(i => 
    i.email.toLowerCase() === userEmail.toLowerCase() && i.role === 'admin' && i.status === 'active'
  );
  const canManage = isOrganizer || isAdmin;

  // Calculate payment stats
  const activeInvitees = invitees.filter(i => i.status === 'active');
  const allParticipants = [
    ...(reminder?.customer_email ? [{
      id: 'organizer',
      email: reminder.customer_email,
      name: null,
      role: 'organizer' as const,
      has_paid: true, // Organizer is considered paid
      contribution_amount: null,
      status: 'active',
      invited_at: '',
      paid_at: null,
      payment_proof_url: null,
    }] : []),
    ...activeInvitees.map(i => ({ ...i, role: i.role || 'participant' as const }))
  ];

  const currency = reminder?.currency || 'INR';
  const targetAmount = reminder?.target_amount || 0;
  const collectedAmount = activeInvitees
    .filter(i => i.has_paid)
    .reduce((sum, i) => sum + (i.contribution_amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CustomerHeader userEmail={userEmail} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
        <CustomerFooter />
      </div>
    );
  }

  if (!reminder) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CustomerHeader userEmail={userEmail} />
        <div className="flex-1 container py-12 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Event Not Found</h1>
          <p className="text-muted-foreground mb-4">This event doesn't exist or has been deleted.</p>
          <Link to="/my-purchases">
            <Button>Go to My Purchases</Button>
          </Link>
        </div>
        <CustomerFooter />
      </div>
    );
  }

  const daysUntil = getDaysUntil();
  const nextDate = getNextOccurrence();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title={`${reminder.event_name} - Contribute`}
        description="Contribute to a group gift"
      />
      <CustomerHeader userEmail={userEmail} onLogout={handleLogout} />

      <main className="flex-1 container pt-16 pb-20 max-w-4xl">
        <Link to="/my-purchases" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          My Dashboard
        </Link>

        {!isVerified ? (
          /* Access Verification */
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-pink-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-pink-600" />
              </div>
              <CardTitle>{reminder.event_name}</CardTitle>
              <CardDescription>
                Enter your email to verify you're part of this event
              </CardDescription>
            </CardHeader>
            <CardContent className="max-w-sm mx-auto space-y-4">
              <Input
                type="email"
                placeholder="Your email address"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
              />
              <Input
                placeholder="Your name (optional)"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
              <Button onClick={verifyAccess} className="w-full">
                <CheckCircle className="w-4 h-4 mr-2" />
                Verify & Join
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* GoFundMe-style Header Card */}
            <Card className="overflow-hidden">
              <div className={`bg-gradient-to-r ${GIFT_COLORS.gradient} p-6 text-white`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <Badge className="bg-white/20 text-white mb-2">
                      <Gift className="w-3 h-3 mr-1" />
                      {reminder.is_recurring ? "Annual Gift" : "Gift Event"}
                    </Badge>
                    <h1 className="text-2xl font-bold">{reminder.event_name}</h1>
                    {reminder.recipient_name && (
                      <p className="text-white/80 mt-1">For {reminder.recipient_name}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{daysUntil}</div>
                    <div className="text-sm text-white/80">days to go</div>
                  </div>
                </div>
                
                {nextDate && (
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{format(nextDate, "EEEE, MMMM d, yyyy")}</span>
                  </div>
                )}
              </div>

            </Card>

            {/* Action Bar */}
            <DiscussionActionBar
              entityType="event"
              entityId={reminderId!}
              entityTitle={reminder.event_name}
              role={isOrganizer ? 'organizer' : isAdmin ? 'admin' : 'participant'}
              themeColor="pink"
              inviteLinkEnabled={reminder.invite_link_enabled}
              inviteLinkToken={reminder.invite_link_token}
              inviteLinkApprovalMode={reminder.invite_link_approval_mode as 'auto' | 'manual'}
              userEmail={userEmail}
              reminderHoursBefore={reminder.reminder_hours_before || []}
              onRefresh={fetchReminder}
              onLeaveSuccess={() => navigate('/my-purchases')}
            />

            {/* Tabbed Discussion Area */}
            <Card>
              <Tabs defaultValue="ideas" className="w-full">
                <TabsList className="w-full grid grid-cols-3 h-12">
                  <TabsTrigger value="ideas" className="gap-2">
                    <Lightbulb className="w-4 h-4" />
                    <span className="hidden sm:inline">Ideas</span>
                  </TabsTrigger>
                  <TabsTrigger value="budget" className="gap-2">
                    <DollarSign className="w-4 h-4" />
                    <span className="hidden sm:inline">Budget</span>
                  </TabsTrigger>
                  <TabsTrigger value="people" className="gap-2">
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">People</span>
                  </TabsTrigger>
                </TabsList>
                
                <CardContent className="pt-4">
                  
                  <TabsContent value="ideas" className="mt-0">
                    <DiscussionIdeas
                      entityType="event"
                      entityId={reminderId!}
                      userEmail={userEmail}
                      userName={userName}
                      occasion={reminder.occasion}
                      themeColor="pink"
                    />
                  </TabsContent>
                  
                  <TabsContent value="budget" className="mt-0">
                    <DiscussionBudget
                      entityType="event"
                      entityId={reminderId!}
                      userEmail={userEmail}
                      participantCount={allParticipants.length}
                      currency={currency}
                      themeColor="pink"
                    />
                  </TabsContent>
                  
                  <TabsContent value="people" className="mt-0">
                    <DiscussionPeople
                      entityType="event"
                      entityId={reminderId!}
                      userEmail={userEmail}
                      userName={userName}
                      organizerEmail={reminder.customer_email || ''}
                      organizerName={null}
                      participants={activeInvitees.map(i => ({
                        id: i.id,
                        email: i.email,
                        name: i.name,
                        status: i.status,
                        role: i.role,
                        has_paid: i.has_paid,
                        contribution_amount: i.contribution_amount,
                        paid_at: i.paid_at,
                      }))}
                      isOrganizer={isOrganizer}
                      isAdmin={isAdmin}
                      currency={currency}
                      targetAmount={reminder.target_amount}
                      splitType={reminder.split_type}
                      themeColor="pink"
                      onRefresh={() => { fetchReminder(); fetchInvitees(); }}
                    />
                  </TabsContent>
                </CardContent>
              </Tabs>

              {/* Collect Payment CTA - Only for Organizers/Admins */}
              {canManage && (
                <div className="mt-4 p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Want to collect payment for this gift?</p>
                        <p className="text-sm text-muted-foreground">Create a group payment to split the cost</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setCollectPaymentDialogOpen(true)}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Collect Payment
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Participant Limit Dialog */}
            <ParticipantLimitDialog
              open={participantLimitDialogOpen}
              onOpenChange={setParticipantLimitDialogOpen}
              entityType="event"
              entityId={reminderId!}
              currentParticipants={allParticipants.length}
              maxFreeParticipants={reminder.max_free_participants ?? FREE_PARTICIPANT_LIMIT_CONST}
              paidSlots={reminder.paid_participant_slots ?? 0}
              organizerEmail={reminder.customer_email || ''}
              participantEmail={!isOrganizer ? userEmail : undefined}
              onPaymentComplete={() => { fetchReminder(); fetchInvitees(); }}
            />

            {/* Collect Payment Dialog */}
            <AddGroupPaymentDialog
              open={collectPaymentDialogOpen}
              onOpenChange={setCollectPaymentDialogOpen}
              organizerEmail={reminder.customer_email || userEmail}
              organizerName={undefined}
              prefilledTitle={`Contribute to ${reminder.event_name}`}
              prefilledParticipants={activeInvitees
                .filter(i => i.status === 'active')
                .map(i => ({ email: i.email, name: i.name || i.email.split('@')[0] }))}
            />

            {/* Share Section */}
            <SocialShareSection
              shareUrl={window.location.href}
              category="gifting"
            />

          </div>
        )}
      </main>
      
      <footer className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/50 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/90">
        <div className="container py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5 text-customer" />
              <span className="font-semibold text-foreground text-xs">Shakatah</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventDiscussion;
